import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Rocket, CheckCircle2, Package } from 'lucide-react';

const PublishDashboard: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ready' | 'published'>('ready');

  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products, activeTab]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Tab bazlı filtreleme
    if (activeTab === 'ready') {
      // Yayın onayı bekleyen ürünler (publish_pending)
      // TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak
      filtered = filtered.filter(p =>
        p.status === 'publish_pending' ||
        (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
      );
    } else if (activeTab === 'published') {
      // Yayınlanmış ürünler (aktif olanlar — kapatılmış/isActive=false hariç)
      filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
    }

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Arama filtresi
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.productionInfo?.productName?.toLowerCase().includes(searchLower) ||
        p.nebimInfo?.sku?.toLowerCase().includes(searchLower) ||
        p.productionInfo?.purchaseCode?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredProducts(filtered);
  };

  const handlePublishClick = async (productId: string) => {
    try {
      await axios.post(`/products/${productId}/publish`, {
        notes: 'Ürün yayınlandı'
      });

      toast.success('✅ Ürün başarıyla yayınlandı (LIVE)!');
      loadProducts();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ürün yayınlanamadı!';
      toast.error(errorMsg);
      console.error('Publish error:', error);
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  // İstatistik kartları
  // TODO: migration sonrası readyCount'taki image_upload OR koşulu kaldırılacak
  const readyCount = products.filter(p =>
    p.status === 'publish_pending' ||
    (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
  ).length;
  const publishedCount = products.filter(p => p.status === 'published' && p.isActive !== false).length;

  if (loading) {
    return (
      <AppLayout title="Yayınlama Paneli" subtitle="Ürünleri yayınlayın ve canlı sisteme aktarın">
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Yükleniyor...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Yayınlama Paneli" subtitle="Ürünleri yayınlayın ve canlı sisteme aktarın">
      <div className="container mx-auto px-4 py-6">
        {/* Başlık */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Rocket className="h-8 w-8" />
              Yayınlama Paneli
            </h1>
            <p className="text-muted-foreground mt-1">
              Ürünleri yayınlayın ve canlı sisteme aktarın
            </p>
          </div>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeTab === 'ready' ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => setActiveTab('ready')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Yayına Hazır</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{readyCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Görselleri yüklenmiş, yayınlanmayı bekliyor
                  </p>
                </div>
                <Package className="h-12 w-12 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeTab === 'published' ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => setActiveTab('published')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Yayınlandı (LIVE)</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{publishedCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Canlı sistemde görünüyor
                  </p>
                </div>
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Göstergesi */}
        <div className="mb-4 flex items-center gap-2">
          <Badge variant={activeTab === 'ready' ? 'default' : 'outline'}>
            {activeTab === 'ready' ? 'Yayına Hazır Ürünler' : 'Yayınlanmış Ürünler'}
          </Badge>
          <span className="text-sm text-muted-foreground">
            ({filteredProducts.length} ürün)
          </span>
        </div>

        {/* Filtreler */}
        <FilterBar 
          filters={filters} 
          onFilterChange={(key: string, value: string) => {
            setFilters(prev => ({ ...prev, [key]: value }));
          }} 
          products={products}
        />

        {/* Ürün Tablosu */}
        <div className="bg-card rounded-lg shadow">
          <ProductTable
            products={filteredProducts}
            onRefresh={loadProducts}
            onPublish={activeTab === 'ready' ? handlePublishClick : undefined}
            onClosureRequest={handleClosureRequest}
            showAllActions
          />
        </div>
      </div>
    </AppLayout>
  );
};

export default PublishDashboard;

